import React, { useState, useEffect } from "react";
import { Stethoscope, CheckCircle2, XCircle } from "lucide-react";
import api from "../api";
import { T, primaryBtn } from "../theme";
import { CloseX } from "./shared";

export default function HealthCheckCard({ profile, onProfileUpdate }) {
  const [showModal, setShowModal] = useState(false);
  const result = profile.health_check;

  return (
    <>
      <div data-testid="health-check-card" style={{ background: T.surface, border: `1px solid ${result ? T.line : `${T.teal}66`}`, borderRadius: 16, padding: 18, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Stethoscope size={18} color={T.teal} />
            <div>
              <div style={{ fontFamily: T.fontHead, fontSize: 15 }}>Controllo salute Instagram</div>
              <div style={{ fontSize: 12, color: T.textDim }}>
                {result
                  ? `Ultimo controllo: ${new Date(result.date).toLocaleDateString("it-IT")} · punteggio ${result.score}/100`
                  : "Una diagnosi iniziale del profilo: 6 domande, un punteggio e le correzioni concrete da cui partire."}
              </div>
            </div>
          </div>
          <button data-testid="health-check-open-btn" onClick={() => setShowModal(true)} style={{ ...primaryBtn, background: T.teal, flexShrink: 0 }}>
            {result ? "Rifai il controllo" : "Fai il controllo"}
          </button>
        </div>
        {result && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line}`, display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div data-testid="health-check-score" style={{ textAlign: "center", minWidth: 80 }}>
              <div style={{ fontFamily: T.fontHead, fontSize: 34, color: result.score >= 70 ? T.teal : result.score >= 40 ? T.amber : T.rose, lineHeight: 1 }}>{result.score}</div>
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>su 100</div>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              {result.corrections.length === 0 ? (
                <div style={{ fontSize: 13, color: T.teal }}>Il tuo profilo è in ottima forma: le basi ci sono tutte. Ora conta la costanza — segui il piano in "Crescita".</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: T.textDim, marginBottom: 8 }}>Le correzioni da cui partire, in ordine di priorità:</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {result.corrections.map((c, i) => (
                      <div key={i} data-testid={`health-check-fix-${i}`} style={{ fontSize: 13, display: "flex", gap: 8, lineHeight: 1.5 }}>
                        <span style={{ color: T.teal, fontFamily: T.fontHead, flexShrink: 0 }}>{i + 1}.</span>{c}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {showModal && <HealthCheckModal profile={profile} onProfileUpdate={onProfileUpdate} onClose={() => setShowModal(false)} />}
    </>
  );
}

function HealthCheckModal({ profile, onProfileUpdate, onClose }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/health-check-questions").then(({ data }) => {
      setQuestions(data.questions);
      const prev = profile.health_check?.answers || {};
      setAnswers(data.questions.reduce((acc, q) => ({ ...acc, [q.key]: prev[q.key] ?? null }), {}));
    });
  }, [profile.health_check]);

  const allAnswered = questions && questions.every((q) => answers[q.key] !== null);

  async function submit() {
    setSaving(true);
    try {
      const { data } = await api.post(`/profiles/${profile.id}/health-check`, { answers });
      onProfileUpdate(data);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,30,51,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div className="modal-card" data-testid="health-check-modal" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 14, padding: 28, width: 540, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 50px rgba(30,30,60,0.3)", position: "relative" }}>
        <CloseX onClick={onClose} />
        <div style={{ fontFamily: T.fontHead, fontSize: 18, marginBottom: 6 }}>Controllo salute Instagram</div>
        <div style={{ fontSize: 13, color: T.textDim, marginBottom: 20 }}>Rispondi guardando il tuo profilo Instagram così com'è oggi: nessuna risposta è sbagliata, serve solo a capire da dove partire.</div>
        {!questions ? (
          <div style={{ fontSize: 13, color: T.textDim }}>Caricamento…</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 22 }}>
            {questions.map((q, i) => (
              <div key={q.key} data-testid={`health-question-${q.key}`} style={{ background: T.bg, border: `1px solid ${T.line}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 10 }}><span style={{ color: T.textDim, marginRight: 6 }}>{i + 1}.</span>{q.question}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button data-testid={`health-answer-yes-${q.key}`} onClick={() => setAnswers({ ...answers, [q.key]: true })} style={{
                    display: "flex", alignItems: "center", gap: 5, borderRadius: 20, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                    background: answers[q.key] === true ? T.teal : "transparent", color: answers[q.key] === true ? T.ink : T.textDim,
                    border: `1px solid ${answers[q.key] === true ? T.teal : T.line}`, fontWeight: answers[q.key] === true ? 700 : 400,
                  }}><CheckCircle2 size={12} /> Sì</button>
                  <button data-testid={`health-answer-no-${q.key}`} onClick={() => setAnswers({ ...answers, [q.key]: false })} style={{
                    display: "flex", alignItems: "center", gap: 5, borderRadius: 20, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                    background: answers[q.key] === false ? T.rose : "transparent", color: answers[q.key] === false ? T.ink : T.textDim,
                    border: `1px solid ${answers[q.key] === false ? T.rose : T.line}`, fontWeight: answers[q.key] === false ? 700 : 400,
                  }}><XCircle size={12} /> Non ancora</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button data-testid="health-check-submit-btn" disabled={!allAnswered || saving} onClick={submit}
          style={{ ...primaryBtn, background: T.teal, width: "100%", padding: "11px 0", opacity: allAnswered && !saving ? 1 : 0.5 }}>
          {saving ? "Un momento…" : "Calcola il punteggio"}
        </button>
      </div>
    </div>
  );
}
